// composables
import { ref, computed, type Ref } from 'vue'
// utilities
import { alertToast } from '@/utilities/notification'
import { useI18n } from 'vue-i18n'
// composables
import usePhoneValidator from '@/composables/usePhoneValidator'
import { isEmail } from '@/utilities/string/isEmail'
// types
import type { Agreement } from '@/types/domain/agreement'

/**
 * Composable for managing agreement contact preferences and details
 * Handles contact permissions, phone validation, and contact details display
 */
export default function useAgreementContact(
  agreementData: Ref<Agreement | null>,
  updateAgreementData: (data: Record<string, unknown> | string) => Promise<unknown>
) {
  const { t } = useI18n()

  // state
  const contactPreferencesIsOpen = ref(false)

  // phone validators
  const phoneNumberValidations = {
    mobile: usePhoneValidator(),
    landline: usePhoneValidator()
  }

  /**
   * Validates phone numbers from agreement data
   */
  function validatePhoneNumbers(): void {
    if (agreementData.value) {
      phoneNumberValidations.mobile.validatePhoneNumber(agreementData.value.customer.contact.mobile ?? '')
      phoneNumberValidations.landline.validatePhoneNumber(agreementData.value.customer.contact.telephone1 ?? '')
    }
  }

  /**
   * Submits contact preferences update
   */
  async function submitContactPreferences(): Promise<void> {
    if (!agreementData.value) {
      return
    }

    const jsonData = JSON.stringify({
      canCall: agreementData.value.contactPermissions.canCall,
      canEmail: agreementData.value.contactPermissions.canEmail,
      canSms: agreementData.value.contactPermissions.canSms,
      canMail: agreementData.value.contactPermissions.canMail
    })

    try {
      await updateAgreementData(jsonData)
      contactPreferencesIsOpen.value = false
      alertToast('Contact Preferences Updated', null, 'success')
    } catch (err) {
      alertToast('Failed to update contact preferences', (err as Error).message, 'danger')
      console.error(err)
    }
  }

  /**
   * Computed contact details list with validation and action flags
   */
  const contactDetails = computed(() => {
    if (!agreementData.value) {
      return []
    }

    const items: Array<{
      type: string
      icon: string
      label: string
      value: string
      isValid: boolean | null
      validationMessage: string | null
      canCall: boolean
      canSendSms: boolean
      canSendEmail: boolean
    }> = []

    if (agreementData.value.customer.contact.telephone1) {
      items.push({
        type: 'landline',
        icon: 'phone',
        label: t('ui.entities.comms.landline'),
        value: agreementData.value.customer.contact.telephone1,
        isValid: phoneNumberValidations.landline.validatedData.value?.possible ?? null,
        validationMessage: phoneNumberValidations.landline.validatedData.value?.possibility ?? null,
        canCall: true,
        canSendSms: false,
        canSendEmail: false
      })
    }

    if (agreementData.value.customer.contact.mobile) {
      items.push({
        type: 'mobile',
        icon: 'mobile',
        label: t('ui.entities.comms.mobile'),
        value: agreementData.value.customer.contact.mobile,
        isValid: phoneNumberValidations.mobile.validatedData.value?.possible ?? null,
        validationMessage: phoneNumberValidations.mobile.validatedData.value?.possibility ?? null,
        canCall: true,
        canSendSms: true,
        canSendEmail: false
      })
    }

    if (agreementData.value.customer.contact.email) {
      const isValid = isEmail(agreementData.value.customer.contact.email)
      items.push({
        type: 'email',
        icon: 'at',
        label: t('ui.entities.comms.email'),
        value: agreementData.value.customer.contact.email,
        isValid: isValid,
        validationMessage: isValid ? null : 'Invalid email address',
        canCall: false,
        canSendSms: false,
        canSendEmail: true
      })
    }

    return items
  })

  return {
    // state
    contactPreferencesIsOpen,
    phoneNumberValidations,
    contactDetails,
    // methods
    validatePhoneNumbers,
    submitContactPreferences
  }
}
